<template>
	<div class="storeEventsTimeline">
		<EventsTimeline
			:events="allEvents"
			:products="products"
			:click-to-create="isEventSidebarVisible ? false : 'single'"
			:selected-date="selectedDate"
			:visible-range-start-date="visibleRangeStartDate"
			:no-event-interactions="isEventSidebarVisible"
			:store="store.id"
			:is-loading-sku-data="isLoadingSKUData"
			:selected-sku-ids="selectedSKUIds"
			:timeline-size="timelineSize"
			:resolution="timelineResolution"
			:is-data-old="isDataOld"
			@event-click="onEventClick"
			@event-create="onEventCreate"
			@event-create-failed="onEventCreateFailed"
			@event-update="onEventUpdate"
			@toggle-product="toggleProductCollapsedState"
			@toggle-all-products="toggleAllSkus"
		/>
	</div>
</template>

<script setup lang="ts">
import { SPECIAL_EVENT_TEMPLATE_ID } from '@bluebrick/discounts-service-module'
import { stringifyFieldForTelemetry } from '@bluebrick/saas-ui'
import {
	type MbscCalendarEventData,
	type MbscEventCreateEvent,
	type MbscEventCreateFailedEvent,
	type MbscEventUpdateEvent,
	type MbscResource
} from '@mobiscroll/vue'
import { groupBy } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, ref, toRefs } from 'vue'
import { convertToDateTime, excludeMobiscrollEndDate } from '@/common/dateUtils'
import { eventDataToTimelineEvent, getSortAlphabeticallyByFieldComparator, isEventVisible, isSpecialEventTemplate } from '@/common/utils'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import EventsTimeline from '@/features/events/components/timeline/EventsTimeline.vue'
import { useEventsStore } from '@/features/events/useEventsStore'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useStoreDateRanges } from '@/features/slicers/useStoreDateRanges'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { type EventData, SidebarContent, type ProductResource, type SkuData, type TimelineEvent, type Store } from '@/types'

const { timelineResolution, timelineSize, isShowingNotesOnly, isShowingScrapedOnly, isUsingStoreSpecificTimeZones } =
	storeToRefs(usePreferencesStore())
const { trackEvent } = useTelemetry()
const { selectedSKUIdsForStore, isLoadingSKUData } = storeToRefs(useEventsStore())
const { selectAllSKUs, unselectAllSKUs } = useEventsStore()
const { createEventFromEmptySlot, createEventFromSpecialOpportunity, updateEventOrCreateFromExisting, reset } = useEventDetailsStore()
const { isSidebarVisible } = storeToRefs(useSidebarStore())
const { closeEventSidebar } = useSidebarStore()

const props = defineProps<{
	skus: SkuData[]
	store: Store
	isDataOld?: boolean
}>()

const { skus, store } = toRefs(props)

const { selectedDate, visibleRangeStartDate } = useStoreDateRanges(store)

const collapsedProducts = ref(new Set<string>())

const products = computed(() => {
	const skusCopy = [...skus.value]
	const specialEventTemplateSKUIndex = skusCopy.findIndex(isSpecialEventTemplate)

	let result: MbscResource[] = []

	if (specialEventTemplateSKUIndex > -1) {
		skusCopy.splice(specialEventTemplateSKUIndex, 1)
		result.push({ id: SPECIAL_EVENT_TEMPLATE_ID, fixed: true })
	}

	// At this point SKUs are already sorted by product name
	const byProduct = groupBy(skusCopy, 'product_name')

	result = result.concat(
		Object.entries(byProduct).reduce<MbscResource[]>((acc, [productName, productSkus]) => {
			acc.push({
				id: productName,
				name: productName,
				eventCreation: false,
				collapsed: collapsedProducts.value.has(productName),
				children: productSkus
					.map((sku) => ({
						id: sku.unique_sku_id,
						name: sku.human_name,
						skuData: sku,
						cssClass: isLoadingSKUData.value(sku.unique_sku_id) ? 'is-loading' : undefined
					}))
					.sort(getSortAlphabeticallyByFieldComparator('name'))
			})

			return acc
		}, [])
	)

	return result
})

const allEvents = computed(() => {
	const result: TimelineEvent[] = []
	const timeZone = isUsingStoreSpecificTimeZones.value ? (store.value.timeZone ?? 'local') : 'local'

	for (const sku of skus.value) {
		const hashToEventMap = sku.events.reduce<Record<string, EventData>>((result, event) => {
			result[event.hash] = event

			return result
		}, {})

		for (const event of sku.events) {
			if (isEventVisible(event, hashToEventMap, isShowingNotesOnly.value, isShowingScrapedOnly.value)) {
				result.push(eventDataToTimelineEvent(event, sku.unique_sku_id, store.value.id, timeZone))
			}
		}
	}

	return result
})

// Number of all (favourite) skus in store
const numberOfUserSkus = computed(() => skus.value?.filter(({ unique_sku_id }) => unique_sku_id !== SPECIAL_EVENT_TEMPLATE_ID).length ?? 0)

const numberOfSelectedSkus = computed(() => selectedSKUIdsForStore.value(store.value.id)?.length ?? 0)

const isEventSidebarVisible = computed(() => isSidebarVisible.value(SidebarContent.EVENT_SETUP))

const selectedSKUIds = computed(() => selectedSKUIdsForStore.value(store.value.id))

function onEventCreate({ event, action }: MbscEventCreateEvent) {
	if (isLoadingSKUData.value(String(event.resource))) {
		return
	}

	const isFromDragEvent = action === 'drag'

	if (isFromDragEvent) {
		trackEvent('user', 'Dragged on empty timeline.', {
			startDate: stringifyFieldForTelemetry(event.start),
			endDate: stringifyFieldForTelemetry(event.end),
			store: store.value.displayName
		})
	} else {
		trackEvent('user', 'Clicked an empty slot on timeline.', { day: stringifyFieldForTelemetry(event.start), store: store.value.displayName })
	}

	if (!event.resource) {
		return
	}

	const start = convertToDateTime(event.start, true)
	const endShifted = excludeMobiscrollEndDate(event.end)

	if (!endShifted.ok) {
		return
	}

	const end = convertToDateTime(endShifted.value, true)
	const sku = skus.value.find(({ unique_sku_id }) => unique_sku_id === event.resource)

	if (!sku || !start.ok || (isFromDragEvent && !end.ok)) {
		return
	}

	createEventFromEmptySlot(store.value, sku, start.value, isFromDragEvent && end.ok ? end.value : undefined)
}

/**
 * This function is called in case of an overlap between a new event created by user
 * (by dragging on empty part of the timeline) and some other existing event.
 * In such case we don't show event details panel
 */
function onEventCreateFailed({ event, action, overlap }: MbscEventCreateFailedEvent) {
	const rawEvent = (overlap as TimelineEvent)?.rawEvent

	if (rawEvent && action === 'drag') {
		// #mobiscroll-exclusive-end-dates
		// This can lead to a false positive overlap if we drag a new event to an available day immediately
		// before start of another event. In such case we allow the creation of the event
		const eventEndDate = excludeMobiscrollEndDate(event.end)
		const overlapStartDate = convertToDateTime(overlap.start)

		if (!eventEndDate.ok || !overlapStartDate.ok || eventEndDate.value < overlapStartDate.value) {
			// That was a false positive collision/overlap so we allow event creation
			return
		}

		trackEvent('app', 'Rejecting creation of event which overlapped with another.', {
			overlappedWith: String(rawEvent.id),
			store: store.value.displayName,
			startDate: event.start?.toString(),
			endDate: event.end?.toString()
		})

		// Reset and close the event details panel
		reset()

		closeEventSidebar(true)
	}
}

/**
 * Triggered when user resizes and existing discount event.
 */
function onEventUpdate({ event }: MbscEventUpdateEvent) {
	if (isLoadingSKUData.value(String(event.resource))) {
		return
	}

	const rawEvent = (event as TimelineEvent).rawEvent
	const sku = skus.value.find(({ unique_sku_id }) => unique_sku_id === event.resource)

	trackEvent('app', 'Resized event on timeline.', { id: String(event.id), store: store.value.displayName, event: JSON.stringify(event) })

	if (!event || !sku) {
		return
	}

	const newStartDate = convertToDateTime(event.start)
	const newEndDate = convertToDateTime(event.end)

	if (!newStartDate.ok || !newEndDate.ok) {
		return
	}

	updateEventOrCreateFromExisting(
		store.value,
		// Show the "Edit event" panel with resized event and new, "resized" dates
		{ ...rawEvent, start_date: newStartDate.value, end_date: newEndDate.value },
		sku.unique_sku_id
	)
}

function onEventClick(data: MbscCalendarEventData) {
	if (isEventSidebarVisible.value || isLoadingSKUData.value(String(data.currentResource?.id))) {
		return
	}

	const event = (data.original as TimelineEvent).rawEvent
	const sku = skus.value.find(({ unique_sku_id }) => unique_sku_id === data.resource)

	trackEvent('user', 'Clicked event on timeline.', { id: String(event.id), store: store.value.displayName, event: JSON.stringify(event) })

	if (isSpecialEventTemplate(event)) {
		createEventFromSpecialOpportunity(store.value, event)

		return
	}

	if (!event || !sku) {
		return
	}

	updateEventOrCreateFromExisting(store.value, event, sku.unique_sku_id)
}

const toggleProductCollapsedState = (resource: ProductResource) => {
	trackEvent('user', resource.collapsed ? 'Expanded a product in timeline.' : 'Collapsed a product in timeline.', {
		store: store.value.displayName
	})

	if (resource.collapsed) {
		collapsedProducts.value.delete(String(resource.id))
	} else {
		collapsedProducts.value.add(String(resource.id))
	}
}

const toggleAllSkus = () => {
	if (numberOfSelectedSkus.value === 0 || (numberOfSelectedSkus.value > 0 && numberOfSelectedSkus.value < numberOfUserSkus.value)) {
		selectAllSKUs(store.value.id)
	} else {
		unselectAllSKUs()
	}
}
</script>
